import React, { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { type Control, useForm } from 'react-hook-form'
import { Col, Form, Row } from 'react-bootstrap'
import styles from '../assets/scss/Suppliers.module.scss'
import loaderStyles from '../../common/assets/scss/LoadingSpinner.module.scss'
import LoadingSpinner from '../../common/components/LoadingSpinner'
import { type SupplierTabProps } from '../hooks/useSupplierTabs'
import FormControlTextArea from '../../common/components/FormControlTextArea'
import { handleAsync } from '../../common/utils/asyncHandler'
import MaPanel from '../../common/components/MaPanel'
import { useFormDisabled } from '../../common/components/FormDisabledContext'
import classNames from 'classnames'
import MaNavBar from '../../common/components/MaNavBar'

function BasicDetails ({
  isLoading,
  hasLoadingError,
  supplierData,
  countries,
  currencies,
  isoCountries,
  admins,
  supplierTypes,
  onSubmit,
  onDirtyChange
}: SupplierTabProps): ReactElement {
  const { t } = useTranslation()

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
    watch,
    reset
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    values: supplierData,
    defaultValues: supplierData
  })

  React.useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  React.useEffect(() => {
    if (supplierData) {
      reset(supplierData)
    }
  }, [supplierData, reset])

  const isDisabled = useFormDisabled()

  return (
    <div className={styles['supplier-data-tab']}>
      <MaNavBar
        onSubmit={handleAsync(handleSubmit(onSubmit))}
        formDirty={isDirty}
      />

      <MaPanel
        className={classNames(
          isDisabled ? styles['disabled-panel'] : '',
          'pt-2'
        )}
      >
        <MaPanel.Body>
          {(isLoading || hasLoadingError) && (
            <div className="flex items-center justify-center h-full">
              <div className={loaderStyles.spinner}>
                <LoadingSpinner />
              </div>
            </div>
          )}
          {!isLoading && (
            <>
              <div className={styles['section-title-panel']}>
                <h4 className={styles['section-title']}>
                  {t('suppliers.details')}
                </h4>
              </div>
              <Form onSubmit={handleAsync(handleSubmit(onSubmit))}>
                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.supplierName')}
                      </Form.Label>
                      <Form.Control
                        {...register('supplierName', {
                          required: {
                            value: true,
                            message: t('validation.error.mandatory', {
                              fieldName: t(
                                'suppliers.basicDetails.tab.label.supplierName'
                              )
                            })
                          },
                          disabled: isDisabled,
                          maxLength: {
                            value: 50,
                            message: t('validation.error.string.max_length', {
                              count: 50
                            })
                          }
                        })}
                        isInvalid={!!errors.supplierName}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.supplierName'
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors?.supplierName?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.phone')}
                      </Form.Label>
                      <Form.Control
                        {...register('phone', {
                          required: {
                            value: true,
                            message: t('validation.error.mandatory', {
                              fieldName: t(
                                'suppliers.basicDetails.tab.label.phone'
                              )
                            })
                          },
                          disabled: isDisabled,
                          maxLength: {
                            value: 30,
                            message: t('validation.error.string.max_length', {
                              count: 30
                            })
                          }
                        })}
                        isInvalid={!!errors.phone}
                        className={styles['form-control']}
                        aria-label={t('suppliers.basicDetails.tab.label.phone')}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.phone?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.contactName')}
                      </Form.Label>
                      <Form.Control
                        {...register('contactName', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 150,
                            message: t('validation.error.string.max_length', {
                              count: 150
                            })
                          }
                        })}
                        isInvalid={!!errors.contactName}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.contactName'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.contactName?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.ordersEmail')}
                      </Form.Label>
                      <Form.Control
                        {...register('ordersEmail', {
                          disabled: isDisabled,
                          required: {
                            value: true,
                            message: t('validation.error.mandatory', {
                              fieldName: t(
                                'suppliers.basicDetails.tab.label.ordersEmail'
                              )
                            })
                          },
                          pattern: {
                            value:
                              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                            message: t('validation.error.string.email')
                          },
                          maxLength: {
                            value: 255,
                            message: t('validation.error.string.max_length', {
                              count: 255
                            })
                          }
                        })}
                        isInvalid={!!errors.ordersEmail}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.ordersEmail'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.ordersEmail?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.accountsEmail')}
                      </Form.Label>
                      <Form.Control
                        {...register('accountsEmail', {
                          disabled: isDisabled,
                          required: {
                            value: true,
                            message: t('validation.error.mandatory', {
                              fieldName: t(
                                'suppliers.basicDetails.tab.label.accountsEmail'
                              )
                            })
                          },
                          pattern: {
                            value:
                              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                            message: t('validation.error.string.email')
                          },
                          maxLength: {
                            value: 255,
                            message: t('validation.error.string.max_length', {
                              count: 255
                            })
                          }
                        })}
                        isInvalid={!!errors.accountsEmail}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.accountsEmail'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.accountsEmail?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.website')}
                      </Form.Label>
                      <Form.Control
                        {...register('website', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 100,
                            message: t('validation.error.string.max_length', {
                              count: 100
                            })
                          }
                        })}
                        isInvalid={!!errors.website}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.website'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.website?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.fax')}
                      </Form.Label>
                      <Form.Control
                        {...register('fax', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 30,
                            message: t('validation.error.string.max_length', {
                              count: 30
                            })
                          }
                        })}
                        isInvalid={!!errors.fax}
                        className={styles['form-control']}
                        aria-label={t('suppliers.basicDetails.tab.label.fax')}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.fax?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.abbreviation')}
                      </Form.Label>
                      <Form.Control
                        {...register('abbreviation', {
                          required: {
                            value: true,
                            message: t('validation.error.mandatory', {
                              fieldName: t(
                                'suppliers.basicDetails.tab.label.abbreviation'
                              )
                            })
                          },
                          disabled: isDisabled,
                          maxLength: {
                            value: 20,
                            message: t('validation.error.string.max_length', {
                              count: 20
                            })
                          }
                        })}
                        isInvalid={!!errors.abbreviation}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.abbreviation'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.abbreviation?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.companyRegNo')}
                      </Form.Label>
                      <Form.Control
                        {...register('companyRegNo', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 50,
                            message: t('validation.error.string.max_length', {
                              count: 50
                            })
                          }
                        })}
                        isInvalid={!!errors.companyRegNo}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.companyRegNo'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.companyRegNo?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.address')}
                      </Form.Label>
                      <FormControlTextArea
                        name={'address'}
                        maxLength={200}
                        disabled={isDisabled}
                        required={false}
                        control={control as unknown as Control}
                        value={watch('address') ?? ''}
                        rows={5}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.address'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.address?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={8}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.comments')}
                      </Form.Label>
                      <FormControlTextArea
                        name={'comments'}
                        maxLength={200}
                        disabled={isDisabled}
                        required={false}
                        control={control as unknown as Control}
                        value={watch('comments') ?? ''}
                        rows={5}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.comments'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.comments?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.basicDetails.tab.label.supplierCountryId'
                        )}
                      </Form.Label>
                      <Form.Select
                        {...register('supplierCountryId', {
                          disabled: isDisabled
                        })}
                        defaultValue={supplierData?.supplierCountryId ?? ''}
                        isInvalid={!!errors.supplierCountryId}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.supplierCountryId'
                        )}
                      >
                        <option value={''}>
                          {t('suppliers.label.pleaseSelect')}
                        </option>
                        {isoCountries?.map(country => (
                          <option key={country.id} value={country.id}>
                            {country.name}
                          </option>
                        ))}
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors?.supplierCountryId?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.currencyId')}
                      </Form.Label>
                      <Form.Select
                        {...register('currencyId', { disabled: isDisabled })}
                        isInvalid={!!errors.currencyId}
                        defaultValue={supplierData?.currencyId ?? ''}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.currencyId'
                        )}
                      >
                        <option value={''}>
                          {t('suppliers.label.pleaseSelect')}
                        </option>
                        {currencies?.map(currency => (
                          <option key={currency.id} value={currency.id}>
                            {currency.name}
                          </option>
                        ))}
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors?.currencyId?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.ownerId')}
                      </Form.Label>
                      <Form.Select
                        {...register('ownerId', { disabled: isDisabled })}
                        defaultValue={supplierData?.ownerId ?? ''}
                        isInvalid={!!errors.ownerId}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.ownerId'
                        )}
                      >
                        <option value={''}>
                          {t('suppliers.label.pleaseSelect')}
                        </option>
                        {admins?.map(user => (
                          <option key={user.id} value={user?.id ?? ''}>
                            {user.name}
                          </option>
                        ))}
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors?.ownerId?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.countryId')}
                      </Form.Label>
                      <Form.Select
                        {...register('countryId', { disabled: isDisabled })}
                        defaultValue={supplierData?.countryId ?? ''}
                        isInvalid={!!errors.countryId}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.countryId'
                        )}
                      >
                        <option value={''}>
                          {t('suppliers.label.pleaseSelect')}
                        </option>
                        {countries?.map(country => (
                          <option key={country.id} value={country?.id ?? ''}>
                            {country.name}
                          </option>
                        ))}
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors?.ownerId?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.supplierTypeId')}
                      </Form.Label>
                      <Form.Select
                        {...register('supplierTypeId', {
                          disabled: isDisabled
                        })}
                        defaultValue={supplierData?.supplierTypeId ?? ''}
                        isInvalid={!!errors.supplierTypeId}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.supplierTypeId'
                        )}
                      >
                        <option value={''}>
                          {t('suppliers.label.pleaseSelect')}
                        </option>
                        {supplierTypes?.map(sType => (
                          <option key={sType.id} value={sType?.id ?? ''}>
                            {sType.name}
                          </option>
                        ))}
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors?.supplierTypeId?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('hasCatalog', { disabled: isDisabled })}
                        id={'cbHasCatalog'}
                        isInvalid={!!errors.hasCatalog}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.hasCatalog'
                        )}
                      />

                      <Form.Label
                        htmlFor={'cbHasCatalog'}
                        className={styles['cb-label']}
                      >
                        {t('suppliers.basicDetails.tab.label.hasCatalog')}
                      </Form.Label>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('hasBreakPricing', {
                          disabled: isDisabled
                        })}
                        id={'hasBreakPricing'}
                        isInvalid={!!errors.hasBreakPricing}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.hasBreakPricing'
                        )}
                      />

                      <Form.Label
                        htmlFor={'hasBreakPricing'}
                        className={styles['cb-label']}
                      >
                        {t('suppliers.basicDetails.tab.label.hasBreakPricing')}
                      </Form.Label>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('allowSelfServe', {
                          disabled: isDisabled
                        })}
                        id={'allowSelfServe'}
                        isInvalid={!!errors.allowSelfServe}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.allowSelfServe'
                        )}
                      />

                      <Form.Label
                        htmlFor={'allowSelfServe'}
                        className={styles['cb-label']}
                      >
                        {t('suppliers.basicDetails.tab.label.allowSelfServe')}
                      </Form.Label>
                    </Form.Group>
                  </Col>
                </Row>

                <br />
                <div className={styles['section-title-panel']}>
                  <h4 className={styles['section-title']}>
                    {t('suppliers.orderDetails.title')}
                  </h4>
                </div>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.orderMethod')}
                      </Form.Label>
                      <Form.Select
                        {...register('orderMethod', { disabled: isDisabled })}
                        defaultValue={supplierData?.orderMethod ?? ''}
                        isInvalid={!!errors.orderMethod}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.orderMethod'
                        )}
                      >
                        <option value={''}>
                          {t('suppliers.label.pleaseSelect')}
                        </option>
                        <option value={1}>
                          {t('suppliers.basicDetails.tab.options.orderMethod1')}
                        </option>
                        <option value={5}>
                          {t('suppliers.basicDetails.tab.options.orderMethod2')}
                        </option>
                        <option value={3}>
                          {t('suppliers.basicDetails.tab.options.orderMethod3')}
                        </option>
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors?.orderMethod?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.orderManagerId')}
                      </Form.Label>
                      <Form.Select
                        {...register('orderManagerId', {
                          disabled: isDisabled
                        })}
                        defaultValue={supplierData?.orderManagerId ?? ''}
                        isInvalid={!!errors.orderManagerId}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.orderManagerId'
                        )}
                      >
                        <option value={''}>
                          {t('suppliers.label.pleaseSelect')}
                        </option>
                        {admins?.map(user => (
                          <option key={user.id} value={user?.id ?? ''}>
                            {user.name}
                          </option>
                        ))}
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors?.orderManagerId?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.basicDetails.tab.label.orderCutoffTime')}
                      </Form.Label>
                      <Form.Control
                        {...register('orderCutoffTime', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 150,
                            message: t('validation.error.string.max_length', {
                              count: 150
                            })
                          }
                        })}
                        isInvalid={!!errors.orderCutoffTime}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.orderCutoffTime'
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors?.orderCutoffTime?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('consolidateOrders', {
                          disabled: isDisabled
                        })}
                        id={'consolidateOrders'}
                        isInvalid={!!errors.consolidateOrders}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.consolidateOrders'
                        )}
                      />

                      <Form.Label
                        htmlFor={'consolidateOrders'}
                        className={styles['cb-label']}
                      >
                        {t(
                          'suppliers.basicDetails.tab.label.consolidateOrders'
                        )}
                      </Form.Label>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('serviceOnly', { disabled: isDisabled })}
                        id={'serviceOnly'}
                        isInvalid={!!errors.serviceOnly}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.basicDetails.tab.label.serviceOnly'
                        )}
                      />

                      <Form.Label
                        htmlFor={'serviceOnly'}
                        className={styles['cb-label']}
                      >
                        {t('suppliers.basicDetails.tab.label.serviceOnly')}
                      </Form.Label>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </>
          )}{' '}
        </MaPanel.Body>
      </MaPanel>
    </div>
  )
}

export default BasicDetails
